import React, { useState, useEffect, useCallback } from 'react';
import Select, { SingleValue } from 'react-select';
import { get } from '../../utils/api';
import './index.css';

// Define the structure of the properties and values used in the component.
interface CollectionProperties {
  key_collection: string;
  key_collection_name_field: string;
  value_collection: string;
  value_collection_name_field: string;
}

interface KeyValue {
  key: string;
  value: string;
}

interface ModuleFieldsKeyValueSelectProps {
  properties: CollectionProperties;
  values?: KeyValue[];
  onChange: (options: KeyValue[]) => void;
}

interface Option {
  value: string;
  label: string;
}

const ModuleFieldsKeyValueSelect: React.FC<ModuleFieldsKeyValueSelectProps> = ({ properties, values = [], onChange }) => {
  const [keyOptions, setKeyOptions] = useState<Option[]>([]);
  const [valueOptions, setValueOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<KeyValue[]>(values);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { key_collection, key_collection_name_field, value_collection, value_collection_name_field } = properties;

  // Fetch options for both keys and values
  const fetchData = useCallback(async (collection: string, fieldName: string, setOptions: React.Dispatch<React.SetStateAction<Option[]>>) => {
    setLoading(true);
    try {
      const params = { page: 1, limit: 1000, filter: {}, sortBy: {} };
      const response = await get(`${collection}`, params);
      const options: Option[] = response.data.data.map((item: any) => ({
        value: item[fieldName],
        label: item[fieldName]
      }));
      setOptions(options);
      setLoading(false);
    } catch (error) {
      console.error(`Failed to fetch data from ${collection}:`, error);
      setError(`Failed to load data from ${collection}`);
      setLoading(false);
    }
  }, []);

  // UseEffect to initialize the component's data fetching
  useEffect(() => {
    fetchData(key_collection, key_collection_name_field, setKeyOptions);
    fetchData(value_collection, value_collection_name_field, setValueOptions);
  }, [fetchData, key_collection, key_collection_name_field, value_collection, value_collection_name_field]);

  // Manage selected options updates
  useEffect(() => {
    setSelectedOptions(values.length ? values : [{ key: '', value: '' }]);
  }, [values]);

  const handleAddRow = () => setSelectedOptions([...selectedOptions, { key: '', value: '' }]);
  const handleRemoveRow = (index: number) => {
    const updated = selectedOptions.filter((_, i) => i !== index);
    setSelectedOptions(updated);
    onChange(updated);
  };

  const handleChange = (index: number, value: SingleValue<Option>, isKey: boolean) => {
    const updated = [...selectedOptions];
    if (value) {
      updated[index][isKey ? 'key' : 'value'] = value.value;
    }
    setSelectedOptions(updated);
    onChange(updated);
  };
  const handleRetry = () => {
    setError(null);
    fetchData(key_collection, key_collection_name_field, setKeyOptions);
    fetchData(value_collection, value_collection_name_field, setValueOptions);
  };
  if (loading) return <p>Loading...</p>;
  if (error)
  return (
    <div>
      <p>{error}</p>
      <button onClick={handleRetry} style={{ padding: "8px 16px", cursor: "pointer" }}>Retry</button>
    </div>
  );
  return (
    <div>
      {selectedOptions.map((option, index) => (
        <div key={index} style={{ display: 'flex', marginBottom: '10px' }}>
          <Select
            value={keyOptions.find(o => o.value === option.key)}
            onChange={(selectedKey) => handleChange(index, selectedKey, true)}
            options={keyOptions}
            placeholder="Select Key"
            className="key-select"
            classNamePrefix="select"
          />
          <Select
            value={valueOptions.find(o => o.value === option.value)}
            onChange={(selectedValue) => handleChange(index, selectedValue, false)}
            options={valueOptions}
            placeholder="Select Value"
            className="value-select"
            classNamePrefix="select"
          />
          <button
            type="button"
            onClick={() => handleRemoveRow(index)}
            style={{ marginLeft: '10px' }}
          >
            Remove
          </button>
        </div>
      ))}
      <button type="button" onClick={handleAddRow}>
        Add Row
      </button>
    </div>
  );
};

export default ModuleFieldsKeyValueSelect;
